import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ScheduledReportsEditorUtils, { ScheduledReportsAdminMode, ScheduledReportsFormType, StepEnum, defaultScheduledReportTask} from "./ScheduledReportsEditorUtils";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { isMobile, isTablet } from "react-device-detect";
import { useReportTask } from "../../queries/scheduled-reports/UseReportTask";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import ScheduledReportsProperties from "./scheduled-reports-properties/ScheduledReportProperties";
import { IAvailableNotificationInfoDTO, IEditReportTaskDTO, ReportTypeEnum } from "../../services/reporting-data-service/ReportScheduleModels";
import { useReportNotifications } from "../../queries/scheduled-reports/UseReportNotifications";
import { RecipientActions, recipientActionItems } from "./scheduled-reports-notifications/ScheduledReportsNotificationUtils";
import ScheduledReportsNotifications, { ScheduledReportsNotificationsType } from "./scheduled-reports-notifications/ScheduledReportsNotifications";
import { ScheduledReportsNotificationTabEnum, useScheduledReportsNotificationState } from "./scheduled-reports-notifications/ScheduledReportsNotificationsState";
import ScheduledReportsSummary from "./scheduled-reports-summary/ScheduledReportsSummary";
import { useSaveReportTask } from "../../queries/scheduled-reports/UseSaveReportTask";
import { useReportTaskList } from "../../queries/scheduled-reports/UseReportTaskList";
import Form from "../forms/form/Form";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../loading-zone/LoadingZone";
import SaveAsDialog, { useSaveAsDialogState, bindSaveAsDialog } from "../save-as-dialog/SaveAsDialog";
import StepMenu, { StepDefinition } from "../step-menu/StepMenu";
import { useStepMenuState, StepMenuEditModeEnum } from "../step-menu/StepMenuState";
import MobileStepMenu from "../step-menu/mobile/MobileStepMenu";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";

export type ScheduledReportsEditorCloseType = {
    reason: "cancel" | "save";
    taskID: number | undefined;
};

export type ScheduledReportsEditorProps = {
    organizationID: number,
    groupID: number,
    taskID: number;
    myReports: boolean;
    copy?: boolean;
    onClose: ({ reason }: ScheduledReportsEditorCloseType) => void;
};

export default function ScheduledReportsEditor({
    organizationID,
    groupID,
    taskID,
    myReports,
    copy,
    onClose,
}: ScheduledReportsEditorProps): JSX.Element {

    const [updating, setUpdating] = useState(false);
    const isPhone = isMobile && !isTablet;
    const [userState] = useUserContext();
    const { confirm
        , acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false: true);
    const notificationState = useScheduledReportsNotificationState();
    const saveAsDialog = useSaveAsDialogState();

    const mode = (taskID ?? 0) <= 0 ? 
                ScheduledReportsAdminMode.Add : 
                ScheduledReportsAdminMode.Edit;

    const notificationsRef = useRef<ScheduledReportsNotificationsType>(undefined);


    const {data, status, refetch } = useQueryNotifications(useReportTask)({
        groupID,
        taskID,
        myReports: false
    });

    const {data: referenceData, 
            refetch: refetchRefernceData, 
            status: referenceDataStatus } = useQueryNotifications(useReportNotifications)({
        groupID
    });

    const {data: reportTaskList, } = useQueryNotifications(useReportTaskList)({
        organizationID: organizationID,        
        groupID: groupID,
        myReports: myReports
    });


    const { mutate: saveReportTask/*, status*/ } = useQueryNotifications(useSaveReportTask)({
        groupID: groupID,

        onSuccess: async (data) => {
            acknowledge(t("scheduledReports.saveSuccessText", { 
                                name: data.name
                            }
                        ));

            await refetch();
            await refetchRefernceData();

            onClose({reason: "save", taskID: data.id});
        },
        onError: () => {
            setUpdating(false);
        }
    });

    const form = useForm<ScheduledReportsFormType>({
        mode: 'onChange',
        defaultValues: {...defaultScheduledReportTask
        }
    });

    const {
        getValues,
        setValue,
        //watch,
        formState: {
            dirtyFields,
            //isDirty,
            isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;
    
    const reportTaskFormData = useWatch({ control: control }) as ScheduledReportsFormType;

    const isDirty = ScheduledReportsEditorUtils.formIsDirty(dirtyFields);
    const hasChanges = isDirty && isValid;


    const isLoading = status === 'loading' || 
                    referenceDataStatus === 'loading' || 
                    updating; 

    const propertiesValid = reportTaskFormData.reportType === ReportTypeEnum.GroupMarketing ?
                            reportTaskFormData.overrideTatID !== -1 : 
                            reportTaskFormData.reportConfigID !== -1;

    const notificationsValid = reportTaskFormData.notificationLists.some(o => o.selected) ||
                                reportTaskFormData.recipients.some(o => o.selected);
    
    const stepDefinitions: StepDefinition[] = useMemo(() => {

        const steps = [{
            key: StepEnum.Properties,
            title: t("scheduledReports.steps.details.title"),
            valid: propertiesValid

        }, {
            key: StepEnum.Notifications,
            title: t("scheduledReports.steps.notifications.title"),
                disabled: !propertiesValid,
                valid: notificationsValid
        },{
            key: StepEnum.Summary,
            title: t("setpointAdmin.steps.summary.title"),
            disabled: !propertiesValid || !notificationsValid,
        }];


        return !myReports ? 
                steps : 
                steps.filter(o => o.key !== StepEnum.Notifications);
    }, [
        t,
        propertiesValid,
        notificationsValid,
        myReports
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveChangesButtonState,
            saveAsNewButtonState
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        setSaveAsNewButtonVisible,
        setSaveAsNewButtonLabel,
        setSaveAsNewButtonDisabled
        
    } = useStepMenuState(
        StepMenuEditModeEnum.Create,
        undefined,
        stepDefinitions
    );

    const updateButtonStates = useCallback(() => {

         switch (activeStep) {
            case StepEnum.Properties:
                setSaveChangesButtonLabel(t(mode === ScheduledReportsAdminMode.Add ? 
                        'buttons.saveAsNew' : 
                        'buttons.saveChanges'));
                setSaveAsNewButtonLabel(t('buttons.saveAsNew'));

                if (myReports) {
                    setNextButtonDisabled(!propertiesValid || !notificationsValid);    
                } else {
                    setNextButtonDisabled(!propertiesValid);
                }
                
                break;


            case StepEnum.Notifications:
                setNextButtonDisabled(!notificationsValid);                
                break;


            case StepEnum.Summary:
                setSaveChangesButtonVisible(true);
                setSaveAsNewButtonVisible(mode === ScheduledReportsAdminMode.Edit);
                if (mode === ScheduledReportsAdminMode.Edit) {
                    setSaveAsNewButtonDisabled(!isValid);
                }
                
                setSaveChangesButtonDisabled(!hasChanges);
                break;

            default:
                break;
        }
    }, [
        t,
        mode,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        activeStep,
        propertiesValid,
        notificationsValid,
        hasChanges,
        setSaveAsNewButtonLabel,
        setSaveAsNewButtonVisible,
        isValid,
        setSaveAsNewButtonDisabled,
        myReports
    ]);


    async function onCancelEdit() {

        if (!isDirty || (await getUserExitConfirmation())) {
            onClose({ 
                reason: "cancel", 

                taskID: reportTaskFormData.id

            });
        }
    }

    function onCancelStep() {
        onCancelEdit();
    }

    function onStepChange(fromStep: number, toStep: number) {

        setActiveStep(toStep);
    }

    async function onSaveChanges() {

        const values = getValues();

        const notificationData = await refetchRefernceData();
        
        const model = ScheduledReportsEditorUtils.mapToModel(groupID, 
                                    values, 
                                    data as IEditReportTaskDTO, 
                                    notificationData.data as IAvailableNotificationInfoDTO,
                                    myReports);
        saveReportTask(model);
    }


    function _validateName(value: string): boolean | string {

        return reportTaskList?.find(o => o.name.toLowerCase() === value.trim().toLowerCase()) === undefined ?
            true :
            t('scheduledReports.uniqueNameText') as string; 
            
    }

    async function onSaveAsNew() {
        const configuration = getValues();

        const result = await saveAsDialog.open({
            name: configuration.name,
            configurationType: t("configuration.types.reportTask"),
            validate: _validateName
        });

        if (result.reason === "ok") {
            setValue('name', result.values?.name as string )
            onSaveChanges();
        }
    }


    function onExecuteAction(menuItem: ActionMenuItem) {

        if (activeStep === StepEnum.Notifications) {
            const action = menuItem.action as RecipientActions;
            notificationsRef.current?.executeAction(action);
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    useEffect(() => {

        if (data && referenceData) {

            reset(ScheduledReportsEditorUtils.mapToFormValues(data, 
                            referenceData, 
                            userState.user?.isElynxAdmin ?? false,
                            copy ?? false,
                            myReports,
                            userState.user?.id as number
                            ));
        }
    }, [
        data,
        referenceData,
        userState.user,
        reset,
        copy,
        myReports
    ]);


    useEffect(() => {

         updateButtonStates();
    }, [
        reportTaskFormData, 
        updateButtonStates
    ]);

    useEffect(() => {
        if (isPhone && activeStep === StepEnum.Notifications) {
            setOpenSideSheet(false);
        }
    }, [
        isPhone,
        activeStep,
        setOpenSideSheet
    ]);

    return (
        <LoadingZone isLoading={isLoading}>
            
            {data && referenceData && <Form form={form} sx={{ padding: 0 }}>

                {isPhone && 
                <MobileStepMenu 
                    steps={steps}
                    title={mode === ScheduledReportsAdminMode.Add ? 'New Scheduled Report': reportTaskFormData.name}
                    activeStep={activeStep}
                    onCancel={onCancelEdit}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    saveAsNewButtonState={saveAsNewButtonState}
                    onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                    onSaveChanges={handleSubmit(() => mode === ScheduledReportsAdminMode.Add ? onSaveAsNew() : onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                    viewOptionState={viewOptionState}
                    setViewOptionState={setViewOptionState}
                    onToggleOptionsAction={activeStep === StepEnum.Notifications ? () => setOpenSideSheet(!openSideSheet) : undefined}
                    actions={
                             activeStep === StepEnum.Notifications && notificationState.state.activeTab === ScheduledReportsNotificationTabEnum.Recipients ?
                                 recipientActionItems :
                            []  
                    }
                    onExecuteAction={onExecuteAction}
                >
                    {activeStep === StepEnum.Properties &&
                        <ScheduledReportsProperties 
                            groupID={groupID} 
                            myReports={myReports} />
                    }


                    {activeStep === StepEnum.Notifications &&

                        <ScheduledReportsNotifications 
                            organizationID={organizationID}
                            groupID={groupID} 
                            openSideSheet={openSideSheet} 
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            setViewOptionState={setViewOptionState} 
                            notificationState={notificationState}
                            ref={notificationsRef}

                        />
                    }

                    {activeStep === StepEnum.Summary &&
                        <ScheduledReportsSummary groupID={groupID}/>
                    }

                </MobileStepMenu>
                }


                {!isPhone && <StepMenu
                    landing={landing}
                    steps={steps}
                    title={mode === ScheduledReportsAdminMode.Add ? 'New Scheduled Report': reportTaskFormData.name}
                    nonLinear={true}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    saveAsNewButtonState={saveAsNewButtonState}
                    onCancel={onCancelEdit}
                    onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                    onSaveChanges={handleSubmit(() => mode === ScheduledReportsAdminMode.Add ? onSaveAsNew() : onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                >
                    {activeStep === StepEnum.Properties &&
                        <ScheduledReportsProperties 
                            groupID={groupID} 
                            myReports={myReports} />
                    }

                    {activeStep === StepEnum.Notifications &&

                        <ScheduledReportsNotifications 
                            organizationID={organizationID}
                            groupID={groupID}
                            openSideSheet={openSideSheet}
                            notificationState={notificationState}
                        />
                    }

                    {activeStep === StepEnum.Summary &&
                        <ScheduledReportsSummary groupID={groupID}/>
                    }

                </StepMenu>
                }
            </Form>
            }


            <SaveAsDialog {...bindSaveAsDialog(saveAsDialog)} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </LoadingZone>
    );
}
